var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { createRef } from '@byted/light-render';
import { BinaryTree } from './layout/bindary-tree';
import * as lodash from '../../utils/lodash';
import { SeriesModel } from '../../model';
import { LegendType } from '../../interface/legend';
import { Group, Rect } from '../../element';
import BreadCrumb from '../../widgets/breadcrumb';
import { getRectLabelPosition } from '../../utils/textLayout';
import { registerChart } from '../index';
import Dataset from '../../core/dataset';
import Zoomable from '../../element/zoomable';
import { createRichText } from '../../rich';
import { flatTree } from '../../utils/tree';
import { getValueListWithMinSizeRatio } from '../../utils/math';
var TRANSITION_DURING = 600;
var defaultColorEncoder = function (parent) {
    if (parent) {
        return {
            color: parent.color,
        };
    }
};
/**
 * treemap model
 */
var TreeMap = /** @class */ (function (_super) {
    __extends(TreeMap, _super);
    function TreeMap() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'treemap';
        _this.legendIcon = 'circle';
        _this.legendType = LegendType.DATUM_LEGEND;
        _this._labelGroupRef = createRef();
        _this._animated = true;
        _this.handleDragStart = function () {
            if (!_this.option.panning) {
                return;
            }
            _this.setState({ isPanning: true });
        };
        _this.handleDragMove = function (event) {
            if (!_this.option.panning) {
                return;
            }
            if (_this.state.isZooming) {
                return;
            }
            var dx = event.dx, dy = event.dy;
            var position = _this.state.position;
            if (!_this.state.isPanning && (dx > 5 || dy > 5)) {
                _this.handleDragStart();
                return;
            }
            position[0] += dx;
            position[1] += dy;
            _this.setState({ position: position });
            _this.updateAfterPanAndZoom();
        };
        _this.handleDragEnd = function () {
            if (!_this.option.panning) {
                return;
            }
            setTimeout(function () {
                _this.setState({ isPanning: false });
            }, 0);
        };
        _this.handleZoom = function (event) {
            if (!_this.option.zooming) {
                return;
            }
            if (!_this._animated) {
                return;
            }
            var origin = event.origin, zoom = event.zoom;
            var x = origin[0], y = origin[1];
            var _a = _this.state, scale = _a.scale, position = _a.position;
            var nextScale = scale * zoom;
            var scaleDiff = nextScale - scale;
            if (nextScale <= 0) {
                return;
            }
            position[0] -= ((x - position[0]) / scale) * scaleDiff;
            position[1] -= ((y - position[1]) / scale) * scaleDiff;
            position[0] += event.position[0];
            position[1] += event.position[1];
            _this.setState({
                scale: nextScale,
                position: position,
            });
            _this.updateAfterPanAndZoom();
        };
        return _this;
    }
    TreeMap.prototype.getDefaultOption = function () {
        return __assign(__assign({}, _super.prototype.getDefaultOption.call(this)), { type: 'treemap', name: '', encode: {
                name: '',
                value: '',
            }, shapeStyle: {
                fill: null,
                lineWidth: 1,
                stroke: '#ffffff',
            }, label: {
                show: true,
                position: 'inside',
                distance: 0,
                fontSize: 12,
                color: '#15191D',
                pointerEvents: 'none',
                formatter: function (point) { return point.name; },
            }, hover: {
                shapeStyle: {
                    fill: null,
                    lineWidth: null,
                    stroke: null,
                },
                label: {
                    color: null,
                    fontSize: null,
                },
            }, selected: {
                shapeStyle: {
                    fill: null,
                    lineWidth: null,
                    stroke: null,
                },
                label: {
                    color: null,
                    fontSize: null,
                },
            }, level: {}, breadcrumb: {
                show: true,
                margin: 12,
                activeColor: '#338AFF',
                textStyle: {
                    color: '#999999',
                    fontSize: 12,
                },
            }, minSizeRatio: 0, allowDrilldown: false, selectedMode: 'single', maxLevel: 2, zooming: false, panning: false, visible: true, showLegend: false });
    };
    TreeMap.prototype.getInitialState = function () {
        return {
            isPanning: false,
            isZooming: false,
            path: [],
            prevPath: [],
            points: [],
            tree: [],
            needAnimation: false,
            prevPoints: [],
            boundingRect: null,
            scale: 1,
            position: [0, 0],
        };
    };
    TreeMap.prototype.init = function () {
        var _this = this;
        this._dragEventRect = new Rect({
            key: this.getKey('evnet-rect'),
            fill: '#ffffff',
            getDragOffset: function () {
                return { x: 0, y: 0 };
            },
            onDrag: this.handleDragMove,
            onDragEnd: this.handleDragEnd,
        });
        this._zoomInstance = new Zoomable(this._dragEventRect, {
            onZoomStart: function () { return _this.setState({ isZooming: true }); },
            onZooming: this.handleZoom,
            onZoomEnd: function () { return _this.setState({ isZooming: false }); },
        });
        this.chart.getRenderInstance().addEventElement(this._dragEventRect);
    };
    TreeMap.prototype.layoutPoints = function (layoutPoints, x, y, width, height) {
        var _this = this;
        var points = layoutPoints.slice();
        var values = getValueListWithMinSizeRatio(points.map(function (point) { return point.value; }), this.option.minSizeRation || this.option.minSizeRatio);
        var max = values[0];
        var rects = max > 0
            ? BinaryTree(width, height, values)
            : values.map(function (value, index) {
                return {
                    x: (index * width) / values.length,
                    y: 0,
                    width: width / values.length,
                    height: height,
                };
            });
        points.forEach(function (point, pointIndx) {
            var rect = rects[pointIndx];
            point.x = x + rect.x;
            point.y = y + rect.y;
            point.width = rect.width;
            point.height = rect.height;
            point.visible = true;
            points.push(point);
            if (point.hasChild && !_this.option.allowDrilldown && point.level < _this.option.maxLevel) {
                _this.layoutPoints(point.children, point.x, point.y, point.width, point.height);
            }
        });
    };
    TreeMap.prototype.getPointsTree = function (data, parent) {
        var _this = this;
        var level = parent ? parent.level + 1 : 1;
        var parentPath = parent ? parent.path : [];
        var levelEncodeOption = this.option.level[level] || defaultColorEncoder;
        if (lodash.isFunction(levelEncodeOption)) {
            levelEncodeOption = levelEncodeOption.call(null, parent);
        }
        var encodeOption = __assign(__assign({}, this.option.encode), levelEncodeOption);
        return data
            .sort(function (a, b) {
            var valueField = encodeOption.value;
            return b[valueField] - a[valueField];
        })
            .map(function (node, dataIndex, arr) {
            var path = __spreadArrays(parentPath, [dataIndex]);
            _this._encode.setDataset(new Dataset(arr));
            _this._encode.setOption(encodeOption);
            var encode = _this._encode.getEncodeData(node, dataIndex);
            var point = {
                encode: encode,
                name: encode.name,
                value: encode.value,
                data: node,
                color: encode.color,
                x: 0,
                y: 0,
                width: 0,
                height: 0,
                level: level,
                index: dataIndex,
                path: path,
                selected: _this.selectedItems.has(node),
                visible: false,
                hasChild: node.children && node.children.length > 0,
                children: null,
                labelRef: createRef(),
                shapeRef: createRef(),
            };
            point.children = _this.getPointsTree(node.children || [], point);
            return point;
        });
    };
    TreeMap.prototype.beforeRender = function () {
        var path = this.state.path;
        var points = this.getPointsTree(this._dataset.getData(), null);
        var chartWidth = this.chart.getWidth();
        var chartHeight = this.chart.getHeight();
        var _a = this.getFacetBBox(), top = _a.top, left = _a.left, width = _a.width, height = _a.height;
        var showBreadcrumb = this.option.allowDrilldown && this.option.breadcrumb.show;
        var layoutHeight = height;
        if (showBreadcrumb) {
            layoutHeight =
                height - this.option.breadcrumb.textStyle.fontSize - this.option.breadcrumb.margin;
        }
        this.setState({ tree: points });
        var pathPoints = this.getPointByPath(path).children;
        this.layoutPoints(pathPoints, left, top, width, layoutHeight);
        this._dragEventRect.setAttr({
            x: 0,
            y: 0,
            width: chartWidth,
            height: chartHeight,
        });
        this.setState({
            boundingRect: {
                x: left,
                y: top,
                width: width,
                height: layoutHeight,
            },
            points: flatTree(points).filter(function (point) { return point.visible; }),
        });
        this.option.zooming ? this._zoomInstance.enable() : this._zoomInstance.disable();
        this._dragEventRect.setAttr({ draggable: this.option.panning });
    };
    TreeMap.prototype.afterRender = function () {
        var _a = this.state, prevPath = _a.prevPath, path = _a.path, needAnimation = _a.needAnimation;
        if (prevPath.join('.') !== path.join('.') && needAnimation) {
            this.setState({ needAnimation: false });
            this.chart.refresh();
        }
    };
    TreeMap.prototype.beforeDestroy = function () {
        this.chart.getRenderInstance().removeEventElement(this._dragEventRect);
        this._zoomInstance.destroy();
        delete this._dragEventRect;
        delete this._zoomInstance;
    };
    TreeMap.prototype.update = function (nextOption, nextIndex, mergeOld) {
        _super.prototype.update.call(this, nextOption, nextIndex, mergeOld);
        this.setState(this.getInitialState());
    };
    TreeMap.prototype.getPointByPath = function (path) {
        var ret = {
            name: this.option.name,
            path: [],
            children: this.state.tree,
        };
        for (var i = 0; i < path.length; i++) {
            var index = path[i];
            if (i === 0) {
                ret = this.state.tree[index];
            }
            else {
                ret = ret.children[index];
            }
        }
        return ret;
    };
    TreeMap.prototype.toggleHighlightPoint = function (dataIndex, active) {
        var point = this.state.points[dataIndex];
        if (!point) {
            return;
        }
        var color = point.color, selected = point.selected, shape = point.shapeRef.current, label = point.labelRef.current;
        shape.parentNode.add(shape);
        shape.setAttr(__assign(__assign(__assign({ fill: color }, this.getShapeStyle(this.option.shapeStyle)), (active ? this.getShapeStyle(this.option.hover.shapeStyle) : null)), (selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null)));
        label &&
            label.setAttr(__assign(__assign(__assign({}, this.getTextStyle(this.option.label)), (active ? this.getTextStyle(this.option.hover.label) : null)), (selected ? this.getTextStyle(this.option.selected.label) : null)));
    };
    TreeMap.prototype.updateAfterPanAndZoom = function () {
        var _this = this;
        var _a = this.state, scale = _a.scale, position = _a.position, boundingRect = _a.boundingRect, path = _a.path;
        var pathPoints = this.getPointByPath(path).children;
        this.layoutPoints(pathPoints, boundingRect.x * scale + position[0], boundingRect.y * scale + position[1], boundingRect.width * scale, boundingRect.height * scale);
        this.state.points.forEach(function (point, dataIndex) {
            var x = point.x, y = point.y, width = point.width, height = point.height, shapeRef = point.shapeRef;
            shapeRef.current.setAttr({
                x: x,
                y: y,
                width: width,
                height: height,
            });
            if (point.labelRef.current) {
                point.labelRef.current.setAttr(__assign({ text: _this.getDataLabel(dataIndex), truncate: {
                        outerWidth: width,
                        outerHeight: height,
                    } }, getRectLabelPosition({
                    top: y,
                    left: x,
                    right: x + width,
                    bottom: y + height,
                }, _this.option.label.position, _this.option.label.distance, 'right')));
            }
        });
    };
    TreeMap.prototype.switchPath = function (path) {
        var _this = this;
        var _a = this.state, prevPath = _a.path, prevPoints = _a.points;
        if (path.join('.') === prevPath.join('.')) {
            return;
        }
        this.setState({
            path: path,
            prevPath: prevPath,
            prevPoints: prevPoints,
            needAnimation: true,
            position: [0, 0],
            scale: 1,
        });
        this._animated = false;
        this.chart.refresh();
        setTimeout(function () {
            _this._animated = true;
            _this._labelGroupRef.current.setAttr('display', true);
        }, TRANSITION_DURING);
    };
    TreeMap.prototype.toggleSelectPoint = function (dataIndex, selected) {
        var _a, _b;
        var point = this.state.points[dataIndex];
        if (this.state.isPanning || !point) {
            return;
        }
        if (this.option.allowDrilldown && point.hasChild) {
            return;
        }
        _super.prototype.toggleSelectPoint.call(this, dataIndex, selected);
        var rect = (_a = point.shapeRef) === null || _a === void 0 ? void 0 : _a.current;
        var label = (_b = point.labelRef) === null || _b === void 0 ? void 0 : _b.current;
        rect && rect.parentNode.add(rect);
        rect.setAttr(__assign(__assign({ fill: point.color }, this.getShapeStyle(this.option.shapeStyle)), (selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null)));
        label &&
            label.setAttr(__assign(__assign({}, this.getTextStyle(this.option.label)), (selected ? this.getTextStyle(this.option.selected.label) : null)));
    };
    TreeMap.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey("treemap-label" + this.state.path.join('.')),
            display: !this.state.needAnimation && this._animated,
            ref: this._labelGroupRef,
        });
        var _a = this.option.label, position = _a.position, distance = _a.distance;
        this.state.points.map(function (point, dataIndex) {
            var x = point.x, y = point.y, width = point.width, height = point.height, selected = point.selected;
            if (point.level < _this.option.maxLevel && point.hasChild && !_this.option.allowDrilldown) {
                return;
            }
            var labelPosition = getRectLabelPosition({
                left: x,
                top: y,
                right: x + width,
                bottom: y + height,
            }, position, distance, 'right');
            var label = createRichText(__assign(__assign(__assign(__assign({ key: point.path.join('.'), ref: point.labelRef, text: _this.getDataLabel(dataIndex) }, labelPosition), { truncate: {
                    outerWidth: width,
                    outerHeight: height,
                } }), _this.getTextStyle(_this.option.label)), (selected ? _this.getTextStyle(_this.option.selected.label) : null)));
            group.add(label);
        });
        return group;
    };
    TreeMap.prototype.renderRects = function (points, isPrev) {
        var _this = this;
        var needAnimation = this.state.needAnimation;
        return points
            .filter(function (point) { return point.visible; })
            .sort(function (a, b) { return (b.selected ? -1 : 1); })
            .map(function (point) {
            var x = point.x, y = point.y, width = point.width, height = point.height, color = point.color, selected = point.selected;
            return new Rect(__assign(__assign(__assign({ key: point.path.join('.'), ref: point.shapeRef, x: x,
                y: y,
                width: width,
                height: height, fill: color, strokeNoScale: true, r: _this.option.shapeStyle.borderRadius }, _this.getShapeStyle(_this.option.shapeStyle)), (selected ? _this.getShapeStyle(_this.option.selected.shapeStyle) : null)), { onClick: function () {
                    if (_this.option.allowDrilldown && point.hasChild) {
                        _this.switchPath(point.path);
                    }
                }, onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        title: _this.option.name,
                        point: _this.wrapPoint(point, point.data),
                    });
                }, pointerEvents: isPrev ? 'none' : 'auto', opacity: !isPrev || needAnimation ? 1 : 0, transitionDuration: TRANSITION_DURING, cursor: point.hasChild ? 'pointer' : 'default' }));
        });
    };
    TreeMap.prototype.renderBreadcrumb = function () {
        var _this = this;
        var _a = this.getFacetBBox(), left = _a.left, right = _a.right, bottom = _a.bottom;
        var cx = (left + right) / 2;
        var rootItem = {
            name: this.option.name,
            path: [],
        };
        var breadcrumbList = [rootItem];
        this.state.path.reduce(function (dataPath, currentPath) {
            dataPath.push(currentPath);
            breadcrumbList.push(_this.getPointByPath(dataPath));
            return dataPath;
        }, []);
        return new BreadCrumb({
            key: this.getKey('breadcrumb'),
            x: cx,
            y: bottom,
            align: 'center',
            verticalAlign: 'bottom',
            items: breadcrumbList.map(function (item) { return item.name; }),
            activeColor: this.option.breadcrumb.activeColor,
            textStyle: this.getTextStyle(this.option.breadcrumb.textStyle),
            onItemClick: function (index) { return _this.switchPath(breadcrumbList[index].path); },
        });
    };
    TreeMap.prototype.getPointTransitionOrigin = function (highLevelPoint) {
        var boundingRect = this.state.boundingRect;
        var cx = boundingRect.x + boundingRect.width / 2;
        var cy = boundingRect.y + boundingRect.height / 2;
        var paddingTop = boundingRect.y - highLevelPoint.y;
        var paddingLeft = boundingRect.x - highLevelPoint.x;
        var sx = boundingRect.width / highLevelPoint.width;
        var sy = boundingRect.height / highLevelPoint.height;
        var originX;
        var originY;
        if (sy === 1) {
            originY = cy;
        }
        else {
            var originToTop = paddingTop / (1 - sy);
            originY = highLevelPoint.y + originToTop;
        }
        if (sx === 1) {
            originX = cx;
        }
        else {
            var originToLeft = paddingLeft / (1 - sx);
            originX = highLevelPoint.x + originToLeft;
        }
        return [originX, originY, sx, sy];
    };
    TreeMap.prototype.render = function () {
        var _a = this.state, boundingRect = _a.boundingRect, points = _a.points, prevPoints = _a.prevPoints, path = _a.path, prevPath = _a.prevPath, needAnimation = _a.needAnimation;
        var isNearLevel = Math.abs(path.length - prevPath.length) === 1;
        var hightLevelPath = prevPath.length <= path.length ? path : prevPath;
        var highLevelInddex = lodash.last(hightLevelPath);
        var isDirlldown = path.length > prevPath.length;
        var group = new Group({
            key: this.getKey('treemap'),
            transitionDuration: TRANSITION_DURING,
        });
        if (!isNearLevel) {
            var currentGroup = new Group({
                key: this.getKey(path.join('.')),
                position: [0, 0],
                transitionProperty: 'all',
            });
            var rects = this.renderRects(points, false);
            currentGroup.addAll(rects);
            group.add(currentGroup);
        }
        else {
            var highLevelPoint = isDirlldown ? prevPoints[highLevelInddex] : points[highLevelInddex];
            var _b = this.getPointTransitionOrigin(highLevelPoint), originX = _b[0], originY = _b[1], sx = _b[2], sy = _b[3];
            var currentGroup = new Group({
                key: this.getKey(path.join('.')),
                position: needAnimation
                    ? isDirlldown
                        ? [highLevelPoint.x - boundingRect.x / sx, highLevelPoint.y - boundingRect.y / sy]
                        : [0, 0]
                    : [0, 0],
                transitionDuration: TRANSITION_DURING,
                scale: needAnimation ? (isDirlldown ? [1 / sx, 1 / sy] : [1, 1]) : [1, 1],
            });
            currentGroup.addAll(this.renderRects(points, false));
            var prevGroup = new Group({
                key: this.getKey(prevPath.join('.')),
                transitionDuration: TRANSITION_DURING,
                position: needAnimation
                    ? [0, 0]
                    : isDirlldown
                        ? [-originX * (sx - 1), -originY * (sy - 1)]
                        : [highLevelPoint.x - boundingRect.x / sx, highLevelPoint.y - boundingRect.y / sy],
                scale: needAnimation ? [1, 1] : isDirlldown ? [sx, sy] : [1 / sx, 1 / sy],
            });
            var rects = this.renderRects(prevPoints, true);
            prevGroup.addAll(rects);
            if (isDirlldown) {
                group.add(prevGroup);
                group.add(currentGroup);
            }
            else {
                group.add(currentGroup);
                group.add(prevGroup);
            }
        }
        if (this.option.label.show) {
            group.add(this.renderLabels());
        }
        if (this.option.allowDrilldown && this.option.breadcrumb.show) {
            this.chart.addToTopLevel(this.renderBreadcrumb());
        }
        return group;
    };
    TreeMap.dependencies = [];
    return TreeMap;
}(SeriesModel));
export default TreeMap;
registerChart('treemap', TreeMap);
